import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { QRCodeSVG } from 'qrcode.react';
import {
  Radio,
  Monitor,
  Smartphone,
  Play,
  Pause,
  Eye,
  ArrowRight,
  Trophy,
  Clock,
  Users,
  CheckCircle2,
  RotateCcw,
  Maximize2,
  Minimize2,
  PlusCircle,
  Sliders,
  ChevronDown,
} from 'lucide-react';
import {
  BIBLE_CATEGORIES,
  DIFFICULTY_LEVELS,
} from '../data/verifiedQuestions.ts';
import { UI_TRANSLATIONS, SupportedLanguage } from '../i18n/translations.ts';
import { soundEngine } from '../utils/soundAndSpeech.ts';
import { SCORING_CONFIG, formatDisplayNumber } from '../config/scoringConfig.ts';

interface LiveEventViewProps {
  userUid: string;
  username: string;
  country: string;
  language: SupportedLanguage;
  initialProjectorPin?: string | null;
}

export const LiveEventView: React.FC<LiveEventViewProps> = ({
  userUid,
  username,
  country,
  language,
  initialProjectorPin,
}) => {
  const t = UI_TRANSLATIONS[language] || UI_TRANSLATIONS.en;
  const socketRef = useRef<Socket | null>(null);

  const [roleView, setRoleView] = useState<
    'hub' | 'host' | 'projector' | 'participant' | 'create'
  >(initialProjectorPin ? 'projector' : 'hub');

  const [activePin, setActivePin] = useState(initialProjectorPin || '777119');
  const [liveState, setLiveState] = useState<any | null>(null);
  const [participantNickname, setParticipantNickname] = useState(username);
  const [hasJoinedAsParticipant, setHasJoinedAsParticipant] = useState(false);
  const [participantLockedIdx, setParticipantLockedIdx] = useState<
    number | null
  >(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isFullScreenProjector, setIsFullScreenProjector] = useState(
    Boolean(initialProjectorPin)
  );
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Event Creator Form State
  const [newTitle, setNewTitle] = useState(
    'Sunday Sanctuary Scripture Bowl LIVE'
  );
  const [newOrg, setNewOrg] = useState('Global Berean Church & Youth');
  const [newCategory, setNewCategory] = useState('ALL CATEGORIES');
  const [newDifficulty, setNewDifficulty] = useState('Mixed Difficulty');
  const [newCount, setNewCount] = useState(6);
  const [newBasePoints, setNewBasePoints] = useState<number>(SCORING_CONFIG.SCORE.CORRECT_BASE);
  const [newSpeedBonus, setNewSpeedBonus] = useState<number>(SCORING_CONFIG.SCORE.SPEED_BONUS_MAX);
  const [newTimerEnabled, setNewTimerEnabled] = useState(true);
  const [newCountdownDuration, setNewCountdownDuration] = useState(20);
  const [newMaxParticipants, setNewMaxParticipants] = useState(300);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const socket = io(window.location.origin, {
      transports: ['polling', 'websocket'],
    });
    socketRef.current = socket;

    socket.on('live:state', (state) => {
      setLiveState(state);
      const me = state.leaderboard?.find((p: any) => p.uid === userUid);
      if (state.status === 'question_active') {
        if (!me || !me.hasAnswered) {
          setParticipantLockedIdx(null);
        }
      }
      if (state.status === 'answer_revealed' && me && me.hasAnswered) {
        const sfxKey = `live-${state.roomPin}-q${state.currentQuestionIndex}`;
        if ((me.lastPoints || 0) > 0) {
          soundEngine.playCorrect(sfxKey);
        } else {
          soundEngine.playWrong(sfxKey);
        }
      }
      if (state.status === 'ended' && me && me.rank === 1 && (me.score || 0) > 0) {
        soundEngine.playVictoryFanfare(`live-win-${state.roomPin}`);
      }
    });

    socket.on('live:timer_tick', ({ timeRemaining }) => {
      setLiveState((prev: any) =>
        prev ? { ...prev, timeRemaining } : prev
      );
    });

    // Connect to default or requested LIVE room automatically
    socket.emit(
      'live:connect_room',
      { roomPin: activePin, language },
      (res: any) => {
        if (res?.ok) {
          setLiveState(res.state);
        }
      }
    );

    return () => {
      socket.disconnect();
    };
  }, [activePin, language, userUid]);

  const connectToPin = (
    pin: string,
    targetRole: 'host' | 'projector' | 'participant'
  ) => {
    if (!socketRef.current) return;
    soundEngine.playClick();
    setErrorMsg(null);
    const cleanPin = pin.trim();
    if (!cleanPin) {
      setErrorMsg('Please enter a valid 6-digit Event Room PIN.');
      return;
    }

    socketRef.current.emit(
      'live:connect_room',
      { roomPin: cleanPin, language },
      (res: any) => {
        if (res?.ok) {
          setActivePin(cleanPin);
          setLiveState(res.state);
          setRoleView(targetRole);
          if (targetRole === 'projector') {
            setIsFullScreenProjector(true);
          }
        } else {
          setErrorMsg(res?.error || 'LIVE Event PIN not found.');
        }
      }
    );
  };

  const handleHostAction = (action: string, payload?: any) => {
    if (!socketRef.current || !liveState) return;
    soundEngine.playClick();
    socketRef.current.emit('live:host_action', {
      roomPin: liveState.roomPin,
      action,
      payload,
    });
  };

  const handleJoinAsParticipant = () => {
    if (!socketRef.current || !liveState) return;
    if (!participantNickname.trim()) {
      setErrorMsg('Please enter your display nickname before joining.');
      return;
    }
    soundEngine.playClick();
    setErrorMsg(null);

    socketRef.current.emit(
      'live:join_participant',
      {
        roomPin: liveState.roomPin,
        uid: userUid,
        username: participantNickname.trim(),
        country,
      },
      (res: any) => {
        if (res?.ok) {
          setHasJoinedAsParticipant(true);
          setLiveState(res.state);
        }
      }
    );
  };

  const handleParticipantSubmit = (optionText: string, optionIndex: number) => {
    if (
      !socketRef.current ||
      !liveState ||
      liveState.status !== 'question_active' ||
      participantLockedIdx !== null
    ) {
      return;
    }
    soundEngine.playClick();
    setParticipantLockedIdx(optionIndex);
    socketRef.current.emit(
      'live:submit_answer',
      {
        roomPin: liveState.roomPin,
        uid: userUid,
        answer: optionText,
        optionIndex,
      },
      () => {}
    );
  };

  const handleCreateLiveEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) {
      setErrorMsg('Event title is required.');
      return;
    }
    setIsCreating(true);
    setErrorMsg(null);

    try {
      const res = await fetch('/api/live/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hostUid: userUid,
          hostName: username,
          title: newTitle.trim(),
          organization: newOrg.trim(),
          categories: [newCategory],
          questionFormats: ['Multiple Choice', 'Who Am I?'],
          questionCount: newCount,
          difficulty: newDifficulty,
          timerEnabled: newTimerEnabled,
          countdownDuration: newCountdownDuration,
          narrationEnabled: false,
          musicEnabled: true,
          maxParticipants: newMaxParticipants,
          language,
        }),
      });

      if (!res.ok) {
        throw new Error('Could not create LIVE event');
      }
      const data = await res.json();
      const createdPin = data.event.roomPin;
      setActivePin(createdPin);
      connectToPin(createdPin, 'host');
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to create LIVE event');
    } finally {
      setIsCreating(false);
    }
  };

  // 16:9 PROJECTOR PRESENTATION INTERFACE
  if (roleView === 'projector' && liveState) {
    const joinUrl = `${window.location.origin}/?livePin=${liveState.roomPin}`;
    const q = liveState.currentQuestion;
    const optionColors = [
      'border-[#525252] bg-[#141414] text-white',
      'border-[#737373] bg-[#1C1C1C] text-white',
      'border-amber-500 bg-amber-950/70 text-white',
      'border-emerald-500 bg-emerald-950/70 text-white',
    ];
    const letters = ['A', 'B', 'C', 'D'];

    return (
      <div
        className={`${
          isFullScreenProjector
            ? 'fixed inset-0 z-50 bg-[#141414] text-white overflow-y-auto'
            : 'max-w-7xl mx-auto my-6 rounded-3xl bg-[#141414] text-white border border-[#262626] shadow-2xl overflow-hidden'
        } flex flex-col justify-between p-6 sm:p-10 min-h-[640px]`}
        data-testid="live-projector-display"
      >
        {/* Top Auditorium Header — Responsive Stacking to Prevent Room PIN Overflow */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-[#262626]">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#A3A3A3] truncate">
              <img
                src="/quickibible-icon.png"
                alt="QuickiBible"
                className="w-4 h-4 rounded object-contain shrink-0"
              />
              <span className="truncate">{liveState.organization} · QuickiBible LIVE Presentation</span>
            </div>
            <h1 className="text-xl sm:text-3xl md:text-4xl font-bold text-white mt-1 truncate max-w-full">
              {liveState.title}
            </h1>
          </div>

          <div className="grid grid-cols-2 sm:flex items-center gap-3 w-full md:w-auto">
            <div className="p-3 rounded-xl bg-[#141414] border border-[#262626] text-left sm:text-right min-w-0">
              <div className="text-[10px] sm:text-[11px] text-[#A3A3A3] font-mono">ROOM PIN</div>
              <div className="text-lg sm:text-2xl font-mono font-bold text-amber-400 tracking-wider tabular-nums truncate">
                {liveState.roomPin}
              </div>
            </div>

            <div className="p-3 rounded-xl bg-[#141414] border border-[#262626] text-left sm:text-right min-w-0">
              <div className="text-[10px] sm:text-[11px] text-[#A3A3A3] font-mono">PARTICIPANTS</div>
              <div className="text-lg sm:text-2xl font-mono font-bold text-white tabular-nums truncate">
                {liveState.participantCount || 0}
              </div>
            </div>

            <div className="col-span-2 sm:col-span-1 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsFullScreenProjector(!isFullScreenProjector)}
                className="flex-1 sm:flex-initial p-2.5 rounded-xl bg-[#141414] border border-[#404040] text-[#D4D4D4] hover:text-white flex items-center justify-center gap-1.5 text-xs font-semibold"
                aria-label="Toggle full-screen projector layout"
              >
                {isFullScreenProjector ? (
                  <>
                    <Minimize2 className="w-4 h-4 text-[#A3A3A3] shrink-0" />
                    <span className="sm:hidden">Exit Fullscreen</span>
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4 text-[#A3A3A3] shrink-0" />
                    <span className="sm:hidden">Fullscreen</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsFullScreenProjector(false);
                  setRoleView('host');
                }}
                className="flex-1 sm:flex-initial px-3.5 py-2.5 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] text-xs font-semibold text-[#FAFAFA] whitespace-nowrap"
              >
                Host Controls
              </button>
            </div>
          </div>
        </div>

        {/* Center Stage Content */}
        <div className="my-auto py-8">
          {liveState.status === 'lobby' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="space-y-6">
                <div className="text-sm font-mono uppercase tracking-widest text-amber-400">
                  Auditorium Lobby Open · Scan QR or Enter PIN
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold leading-tight">
                  Join on Your Phone with PIN{' '}
                  <span className="font-mono text-amber-400">
                    {liveState.roomPin}
                  </span>
                </h2>
                <p className="text-lg text-[#D4D4D4]">
                  Hosted by {liveState.hostName} · {liveState.totalQuestions}{' '}
                  Canonical Questions ·{' '}
                  {liveState.timerEnabled
                    ? `${liveState.countdownDuration}s Synchronized Timer`
                    : 'Untimed Host-Paced Mode'}
                </p>
                <div className="pt-2 text-sm font-medium text-[#D4D4D4]">
                  <span className="text-[#A3A3A3] font-mono uppercase text-xs mr-2">
                    Connected Roster:
                  </span>
                  {liveState.leaderboard
                    ?.map((p: any) => `${p.username} (${p.country})`)
                    .join(' · ')}
                </div>
              </div>

              <div className="flex flex-col items-center justify-center">
                <div className="p-6 bg-[#1C1C1C] rounded-3xl border border-[#262626] shadow-2xl">
                  <QRCodeSVG value={joinUrl} size={240} fgColor="#FAFAFA" bgColor="transparent" />
                </div>
                <div className="mt-4 text-sm font-mono text-[#A3A3A3]">
                  PIN: {liveState.roomPin}
                </div>
              </div>
            </div>
          )}

          {(liveState.status === 'question_active' ||
            liveState.status === 'answer_revealed' ||
            liveState.status === 'paused') &&
            q && (
              <div className="space-y-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="text-sm font-mono text-[#A3A3A3] font-semibold">
                    QUESTION {liveState.currentQuestionIndex + 1} OF{' '}
                    {liveState.totalQuestions} · {q.category} · {q.difficulty}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-sm font-mono text-[#D4D4D4]">
                      Locked Answers:{' '}
                      <strong className="text-white tabular-nums">
                        {liveState.answeredCount} / {liveState.participantCount}
                      </strong>
                    </div>
                    <div className="px-5 py-2 rounded-2xl bg-[#262626] border border-[#525252] font-mono text-2xl font-bold text-white tabular-nums">
                      {liveState.timerEnabled
                        ? `${liveState.timeRemaining}s`
                        : 'UNTIMED (HOST PACED)'}
                    </div>
                  </div>
                </div>

                <h2 className="text-3xl sm:text-5xl font-bold text-white leading-tight">
                  {q.questionText}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {q.options.map((opt: string, idx: number) => {
                    const isRevealed = liveState.status === 'answer_revealed';
                    const isCorrect = isRevealed && opt === q.correctAnswer;
                    const countForOpt =
                      liveState.answerDistribution?.[idx] || 0;

                    return (
                      <div
                        key={idx}
                        className={`p-6 rounded-2xl border-2 flex items-center justify-between gap-4 transition-all ${
                          isRevealed
                            ? isCorrect
                              ? 'border-emerald-400 bg-emerald-950/90 text-white scale-[1.01]'
                              : 'border-[#262626] bg-[#141414] opacity-45'
                            : optionColors[idx % 4]
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="w-12 h-12 rounded-xl bg-black/40 border border-white/15 flex items-center justify-center font-mono text-xl font-bold">
                            {letters[idx]}
                          </span>
                          <span className="text-xl sm:text-2xl font-semibold">
                            {opt}
                          </span>
                        </div>

                        {isRevealed && (
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-lg font-bold text-[#FAFAFA] tabular-nums">
                              {countForOpt} votes
                            </span>
                            {isCorrect && (
                              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {liveState.status === 'answer_revealed' && (
                  <div className="p-6 rounded-2xl bg-[#141414] border border-[#525252] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-mono font-bold text-amber-400">
                        {q.scriptureReference} · Correct Answer:{' '}
                        {q.correctAnswer}
                      </div>
                      <p className="text-base text-[#FAFAFA] mt-1">
                        {q.explanation}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

          {(liveState.status === 'leaderboard' ||
            liveState.status === 'ended') && (
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <Trophy className="w-16 h-16 text-amber-400 mx-auto" />
              <h2 className="text-4xl font-bold text-white">
                {liveState.status === 'ended'
                  ? 'Final Winners Podium'
                  : 'Live Event Standings'}
              </h2>
              <div className="space-y-3 text-left">
                {liveState.leaderboard?.slice(0, 6).map((p: any) => (
                  <div
                    key={p.uid}
                    className="flex items-center justify-between p-5 rounded-2xl bg-[#141414] border border-[#262626]"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-mono font-bold text-amber-400 w-10">
                        #{p.rank}
                      </span>
                      <div>
                        <div className="text-xl font-bold text-white">
                          {p.username}
                        </div>
                        <div className="text-xs text-[#A3A3A3]">
                          Country: {p.country} · {p.correctCount} correct
                        </div>
                      </div>
                    </div>
                    <div className={`text-2xl font-mono font-bold tabular-nums ${(p.score || 0) <= 0 ? 'text-[#737373]' : 'text-[#A3A3A3]'}`}>
                      {formatDisplayNumber(p.score || 0).text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Projector Footer */}
        <div className="pt-4 border-t border-[#262626] flex items-center justify-between text-xs text-[#A3A3A3] font-mono">
          <span>Quick!Bible Auditorium Presentation Engine (16:9)</span>
          <span>
            Status: {liveState.status.toUpperCase()} · PIN: {liveState.roomPin}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6">
      {/* Top Mode Switcher Bar — Responsive Collapsible Selector for Mobile & Tablet */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#FAFAFA]">
            {t.liveEventCenter}
          </h1>
          <p className="text-sm text-[#737373] text-[#A3A3A3] mt-1">
            Dedicated Host Control Center, 16:9 Auditorium Projector Screen, and
            Mobile Participant Interface.
          </p>
        </div>

        {/* Mobile & Tablet Collapsible Menu (< md) */}
        <div className="w-full md:hidden relative">
          <button
            type="button"
            onClick={() => setIsMobileNavOpen((prev) => !prev)}
            className="w-full min-h-[46px] px-4 py-3 rounded-2xl bg-[#141414] border border-[#525252] shadow-sm flex items-center justify-between text-[#FAFAFA] text-xs font-bold transition-all active:scale-[0.99]"
          >
            <div className="flex items-center gap-2.5">
              {roleView === 'hub' && <Radio className="w-4 h-4 text-[#A3A3A3] shrink-0" />}
              {roleView === 'host' && <Sliders className="w-4 h-4 text-[#A3A3A3] shrink-0" />}
              {roleView === 'projector' && <Monitor className="w-4 h-4 text-amber-500 shrink-0" />}
              {roleView === 'participant' && <Smartphone className="w-4 h-4 text-emerald-500 shrink-0" />}
              {roleView === 'create' && <PlusCircle className="w-4 h-4 text-[#A3A3A3] shrink-0" />}
              <span className="font-semibold text-xs">
                {roleView === 'hub' && 'LIVE Overview'}
                {roleView === 'host' && 'Host Dashboard'}
                {roleView === 'projector' && '16:9 Projector Display'}
                {roleView === 'participant' && 'Participant Mobile View'}
                {roleView === 'create' && 'Create LIVE Event'}
              </span>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-[#A3A3A3] transition-transform duration-200 ${
                isMobileNavOpen ? 'rotate-180 text-[#A3A3A3]' : ''
              }`}
            />
          </button>

          {isMobileNavOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 z-40 rounded-2xl bg-[#141414] border border-[#262626] shadow-xl overflow-hidden divide-y divide-[#262626]">
              {[
                { id: 'hub', label: 'LIVE Overview', icon: Radio, color: 'text-[#A3A3A3]' },
                { id: 'host', label: 'Host Dashboard', icon: Sliders, color: 'text-[#A3A3A3]' },
                { id: 'projector', label: '16:9 Projector Display', icon: Monitor, color: 'text-amber-500' },
                { id: 'participant', label: 'Participant Mobile View', icon: Smartphone, color: 'text-emerald-500' },
                { id: 'create', label: 'Create LIVE Event', icon: PlusCircle, color: 'text-[#A3A3A3]' },
              ].map((item) => {
                const Icon = item.icon;
                const active = roleView === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      if (item.id === 'projector') {
                        setIsFullScreenProjector(false);
                      }
                      setRoleView(item.id as any);
                      setIsMobileNavOpen(false);
                    }}
                    className={`w-full px-4 py-3 text-left text-xs font-semibold flex items-center justify-between transition-colors ${
                      active
                        ? 'bg-[#1C1C1C] text-[#A3A3A3] font-bold'
                        : 'text-[#D4D4D4] hover:bg-[#1C1C1C]'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Icon className={`w-4 h-4 ${item.color} shrink-0`} />
                      <span>{item.label}</span>
                    </div>
                    {active && <CheckCircle2 className="w-4 h-4 text-[#A3A3A3] shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Desktop Multi-Button Bar (≥ md) */}
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            onClick={() => setRoleView('hub')}
            className={`px-3.5 py-2 min-h-[38px] rounded-xl text-xs font-semibold transition-colors ${
              roleView === 'hub'
                ? 'bg-[#F97316] text-white'
                : 'bg-[#161618] border border-[#27272A] text-[#A1A1AA] hover:text-white'
            }`}
          >
            LIVE Overview
          </button>
          <button
            type="button"
            onClick={() => setRoleView('host')}
            className={`px-3.5 py-2 min-h-[38px] rounded-xl text-xs font-semibold transition-colors ${
              roleView === 'host'
                ? 'bg-[#F97316] text-white'
                : 'bg-[#161618] border border-[#27272A] text-[#A1A1AA] hover:text-white'
            }`}
          >
            Host Dashboard
          </button>
          <button
            type="button"
            onClick={() => {
              setRoleView('projector');
              setIsFullScreenProjector(false);
            }}
            className={`px-3.5 py-2 min-h-[38px] rounded-xl text-xs font-semibold transition-colors flex items-center gap-1.5 ${
              roleView === 'projector'
                ? 'bg-[#F97316] text-white'
                : 'bg-[#161618] border border-[#27272A] text-[#A1A1AA] hover:text-white'
            }`}
          >
            <Monitor className={`w-3.5 h-3.5 ${roleView === 'projector' ? 'text-white' : 'text-[#F97316]'}`} />
            <span>16:9 Projector Display</span>
          </button>
          <button
            type="button"
            onClick={() => setRoleView('participant')}
            className={`px-3.5 py-2 min-h-[38px] rounded-xl text-xs font-semibold transition-colors ${
              roleView === 'participant'
                ? 'bg-[#F97316] text-white'
                : 'bg-[#161618] border border-[#27272A] text-[#A1A1AA] hover:text-white'
            }`}
          >
            Participant Mobile View
          </button>
          <button
            type="button"
            onClick={() => setRoleView('create')}
            className={`px-3.5 py-2 min-h-[38px] rounded-xl text-xs font-semibold transition-colors flex items-center gap-1.5 ${
              roleView === 'create'
                ? 'bg-[#F97316] text-white'
                : 'bg-[#161618] border border-[#27272A] text-[#A1A1AA] hover:text-white'
            }`}
          >
            <PlusCircle className="w-3.5 h-3.5" />
            <span>Create LIVE Event</span>
          </button>
        </div>
      </div>

      {errorMsg && (
        <div
          role="alert"
          className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-400"
        >
          {errorMsg}
        </div>
      )}

      {/* HUB OVERVIEW */}
      {roleView === 'hub' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1: Host Controls */}
          <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm flex flex-col justify-between">
            <div>
              <div className="w-11 h-11 rounded-xl bg-[#1C1C1C] text-[#A3A3A3] flex items-center justify-center mb-4">
                <Sliders className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold text-[#FAFAFA] mb-2">
                1. Host Control Dashboard
              </h2>
              <p className="text-xs text-[#737373] text-[#A3A3A3] leading-relaxed mb-6">
                Control question progression, toggle the authoritative server
                timer ON or OFF, reveal canonical answers, and display standings
                without being forced to play as a participant.
              </p>
            </div>
            <button
              type="button"
              onClick={() => connectToPin(activePin, 'host')}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 text-white text-xs font-semibold transition-colors shadow-lg shadow-orange-950/30"
            >
              Open Host Dashboard (PIN: {activePin})
            </button>
          </div>

          {/* Card 2: 16:9 Projector Display */}
          <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm flex flex-col justify-between">
            <div>
              <div className="w-11 h-11 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center mb-4">
                <Monitor className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold text-[#FAFAFA] mb-2">
                2. 16:9 Auditorium Projector
              </h2>
              <p className="text-xs text-[#737373] text-[#A3A3A3] leading-relaxed mb-6">
                Full-screen presentation interface designed for church
                projectors and second monitors. Features large typography, QR
                joining, live vote distribution bars, and podium animations.
              </p>
            </div>
            <button
              type="button"
              onClick={() => connectToPin(activePin, 'projector')}
              className="w-full py-3 px-4 rounded-xl bg-[#141414] text-[#FAFAFA] text-xs font-semibold transition-colors"
            >
              Launch 16:9 Projector View
            </button>
          </div>

          {/* Card 3: Mobile Participant Join */}
          <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm flex flex-col justify-between">
            <div>
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-4">
                <Smartphone className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold text-[#FAFAFA] mb-2">
                3. Mobile Participant Controller
              </h2>
              <p className="text-xs text-[#737373] text-[#A3A3A3] leading-relaxed mb-4">
                Enter any 6-digit LIVE Event PIN to join from your mobile device
                and submit answers in real time.
              </p>
              <div className="mb-4">
                <label
                  htmlFor="live-pin-input"
                  className="block text-[11px] font-semibold text-[#737373] mb-1"
                >
                  Event Room PIN
                </label>
                <input
                  id="live-pin-input"
                  type="text"
                  value={activePin}
                  onChange={(e) => setActivePin(e.target.value)}
                  className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3 py-2 text-sm font-mono font-bold text-[#FAFAFA]"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => connectToPin(activePin, 'participant')}
              className="w-full py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
            >
              Join Event as Participant
            </button>
          </div>
        </div>
      )}

      {/* HOST CONTROL DASHBOARD */}
      {roleView === 'host' && liveState && (
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          data-testid="live-host-dashboard"
        >
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4 pb-5 mb-5 border-b border-[#262626]">
                <div>
                  <div className="text-xs font-mono text-[#A3A3A3] font-semibold">
                    LIVE ROOM PIN: {liveState.roomPin} · Status:{' '}
                    {liveState.status.toUpperCase()}
                  </div>
                  <h2 className="text-xl font-bold text-[#FAFAFA] mt-0.5">
                    {liveState.title}
                  </h2>
                </div>

                {/* Genuine Server Timer ON/OFF Toggle */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      handleHostAction('toggle_timer', {
                        timerEnabled: !liveState.timerEnabled,
                      })
                    }
                    className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-colors flex items-center gap-2 ${
                      liveState.timerEnabled
                        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                        : 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    <span>
                      {t.timerSwitchLabel}:{' '}
                      {liveState.timerEnabled
                        ? `ON (${liveState.countdownDuration}s)`
                        : 'OFF (Manual Host Advance)'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Host Action Deck */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => handleHostAction('start_question')}
                  className="p-3.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-xs font-semibold flex flex-col items-center gap-1.5"
                >
                  <Play className="w-4 h-4" />
                  <span>
                    {liveState.status === 'lobby'
                      ? 'Start Event'
                      : 'Restart Question'}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    handleHostAction(
                      liveState.status === 'paused'
                        ? 'resume_event'
                        : 'pause_event'
                    )
                  }
                  className="p-3.5 rounded-xl border border-[#262626] text-[#FAFAFA] hover:bg-[#1C1C1C] text-xs font-semibold flex flex-col items-center gap-1.5"
                >
                  <Pause className="w-4 h-4" />
                  <span>
                    {liveState.status === 'paused'
                      ? 'Resume Event'
                      : 'Pause Event'}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => handleHostAction('reveal_answer')}
                  className="p-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex flex-col items-center gap-1.5"
                >
                  <Eye className="w-4 h-4" />
                  <span>Reveal Answer</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleHostAction('next_question')}
                  className="p-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-[#FAFAFA] text-xs font-bold flex flex-col items-center gap-1.5"
                >
                  <ArrowRight className="w-4 h-4" />
                  <span>Next Question</span>
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#262626]">
                <button
                  type="button"
                  onClick={() => handleHostAction('show_leaderboard')}
                  className="px-4 py-2 rounded-xl border border-[#262626] text-xs font-semibold text-[#FAFAFA] hover:bg-[#1C1C1C] flex items-center gap-1.5"
                >
                  <Trophy className="w-3.5 h-3.5 text-amber-500" />
                  <span>Show Leaderboard on Projector</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleHostAction('end_event')}
                  className="px-4 py-2 rounded-xl border border-[#262626] text-xs font-semibold text-[#FAFAFA] hover:bg-[#1C1C1C]"
                >
                  End Event & Announce Podium
                </button>

                <button
                  type="button"
                  onClick={() => handleHostAction('reset_event')}
                  className="px-4 py-2 rounded-xl border border-[#262626] text-xs font-semibold text-[#737373] hover:text-[#FAFAFA] hover:text-[#FAFAFA] flex items-center gap-1.5 ml-auto"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset to Lobby</span>
                </button>
              </div>
            </div>

            {/* Active Question Preview for Host */}
            {liveState.currentQuestion && (
              <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm">
                <div className="flex items-center justify-between text-xs font-mono text-[#737373] mb-2">
                  <span>
                    Question {liveState.currentQuestionIndex + 1} of{' '}
                    {liveState.totalQuestions} (
                    {liveState.currentQuestion.scriptureReference})
                  </span>
                  <span>
                    {liveState.timerEnabled
                      ? `Time Remaining: ${liveState.timeRemaining}s`
                      : 'Timer OFF (Manual Progression)'}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-[#FAFAFA] mb-4">
                  {liveState.currentQuestion.questionText}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {liveState.currentQuestion.options.map(
                    (opt: string, idx: number) => {
                      const votes =
                        liveState.answerDistribution?.[idx] || 0;
                      return (
                        <div
                          key={idx}
                          className="p-3.5 rounded-xl border border-[#262626] flex items-center justify-between text-sm"
                        >
                          <span className="font-medium text-[#FAFAFA]">
                            {opt}
                          </span>
                          <span className="font-mono text-xs font-bold text-[#A3A3A3]">
                            {votes} votes
                          </span>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Live Participant Telemetry */}
          <div className="rounded-2xl bg-[#141414] border border-[#262626] p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-[#FAFAFA] flex items-center gap-2">
                <Users className="w-4 h-4 text-[#A3A3A3]" />
                <span>Participants ({liveState.participantCount})</span>
              </h3>
              <span className="text-xs font-mono text-[#737373]">
                Answered: {liveState.answeredCount}/{liveState.participantCount}
              </span>
            </div>

            <div className="space-y-2.5">
              {liveState.leaderboard?.map((p: any) => (
                <div
                  key={p.uid}
                  className="flex items-center justify-between p-3 rounded-xl border border-[#262626] text-xs"
                >
                  <div>
                    <div className="font-semibold text-[#FAFAFA]">
                      #{p.rank} {p.username} ({p.country})
                    </div>
                    <div className="text-[#737373]">
                      {p.hasAnswered ? 'Locked In' : 'Waiting...'}
                    </div>
                  </div>
                  <div className={`font-mono font-bold tabular-nums ${(p.score || 0) <= 0 ? 'text-[#737373]' : 'text-[#A3A3A3]'}`}>
                    {formatDisplayNumber(p.score || 0).text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PARTICIPANT MOBILE INTERFACE */}
      {roleView === 'participant' && liveState && (
        <div
          className="max-w-md mx-auto rounded-3xl bg-[#141414] border border-[#262626] p-6 shadow-lg"
          data-testid="live-participant-mobile"
        >
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-[#262626]">
            <div>
              <div className="text-[11px] font-mono text-[#A3A3A3] font-semibold">
                PIN: {liveState.roomPin}
              </div>
              <div className="text-sm font-bold text-[#FAFAFA]">
                {liveState.title}
              </div>
            </div>
            <div className="text-right font-mono text-xs">
              <div className="text-[#A3A3A3]">Status</div>
              <div className="font-bold text-[#FAFAFA]">
                {liveState.status.toUpperCase()}
              </div>
            </div>
          </div>

          {!hasJoinedAsParticipant ? (
            <div className="space-y-4">
              <label
                htmlFor="participant-name-input"
                className="block text-xs font-semibold text-[#D4D4D4]"
              >
                Your Participant Nickname
              </label>
              <input
                id="participant-name-input"
                type="text"
                value={participantNickname}
                onChange={(e) => setParticipantNickname(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA]"
              />
              <button
                type="button"
                onClick={handleJoinAsParticipant}
                className="w-full py-3.5 rounded-xl bg-[#525252] hover:bg-[#404040] text-white text-sm font-semibold"
              >
                Enter Auditorium Waiting Room
              </button>
            </div>
          ) : liveState.status === 'lobby' ? (
            <div className="py-10 text-center space-y-3">
              <Radio className="w-10 h-10 text-[#A3A3A3] mx-auto animate-pulse" />
              <h3 className="text-lg font-bold text-[#FAFAFA]">
                You&apos;re In! ({participantNickname})
              </h3>
              <p className="text-xs text-[#737373]">
                Look up at the auditorium projector screen. Waiting for{' '}
                {liveState.hostName} to launch Question 1...
              </p>
            </div>
          ) : liveState.currentQuestion ? (
            <div className="space-y-5">
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-[#737373]">
                  Q{liveState.currentQuestionIndex + 1}/
                  {liveState.totalQuestions}
                </span>
                <span className="font-bold text-[#A3A3A3]">
                  {liveState.timerEnabled
                    ? `${liveState.timeRemaining}s`
                    : 'Untimed Mode'}
                </span>
              </div>

              <h3 className="text-base sm:text-lg font-bold text-[#FAFAFA] text-center leading-relaxed break-words py-2">
                {liveState.currentQuestion.questionText}
              </h3>

              <div className="grid grid-cols-1 gap-3">
                {liveState.currentQuestion.options.map(
                  (opt: string, idx: number) => {
                    const letters = ['A', 'B', 'C', 'D'];
                    const isLocked = participantLockedIdx === idx;
                    const isRevealed =
                      liveState.status === 'answer_revealed';
                    const isRight =
                      isRevealed &&
                      opt === liveState.currentQuestion.correctAnswer;

                    return (
                      <button
                        key={idx}
                        type="button"
                        disabled={
                          participantLockedIdx !== null ||
                          liveState.status !== 'question_active'
                        }
                        onClick={() => handleParticipantSubmit(opt, idx)}
                        className={`p-4 rounded-2xl border-2 text-left font-semibold text-sm flex items-center gap-3 min-h-[58px] transition-all ${
                          isRevealed
                            ? isRight
                              ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300'
                              : isLocked
                              ? 'border-red-500 bg-red-500/15 text-red-300'
                              : 'border-[#262626] opacity-50'
                            : isLocked
                            ? 'border-[#525252] bg-[#525252] text-white'
                            : 'border-[#262626] hover:border-[#525252] text-[#FAFAFA]'
                        }`}
                      >
                        <span className="w-8 h-8 rounded-lg bg-[#1C1C1C] flex items-center justify-center font-mono text-xs font-bold shrink-0">
                          {letters[idx]}
                        </span>
                        <span className="break-words min-w-0 flex-1">{opt}</span>
                      </button>
                    );
                  }
                )}
              </div>

              {participantLockedIdx !== null &&
                liveState.status === 'question_active' && (
                  <div className="p-3 rounded-xl bg-[#1C1C1C] text-[#FAFAFA] text-xs font-mono text-center">
                    Submission locked on server! Waiting for host reveal...
                  </div>
                )}
            </div>
          ) : null}
        </div>
      )}

      {/* EVENT CREATOR FORM */}
      {roleView === 'create' && (
        <form
          onSubmit={handleCreateLiveEvent}
          className="max-w-3xl mx-auto rounded-2xl bg-[#141414] border border-[#262626] p-6 sm:p-8 shadow-sm space-y-6"
        >
          <h2 className="text-xl font-bold text-[#FAFAFA]">
            Create New Auditorium LIVE Event
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label
                htmlFor="live-title-input"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Event Title
              </label>
              <input
                id="live-title-input"
                type="text"
                required
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA]"
              />
            </div>

            <div>
              <label
                htmlFor="live-org-input"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Church / School / Organization
              </label>
              <input
                id="live-org-input"
                type="text"
                value={newOrg}
                onChange={(e) => setNewOrg(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA]"
              />
            </div>

            <div>
              <label
                htmlFor="live-cat-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Category
              </label>
              <select
                id="live-cat-select"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA]"
              >
                {BIBLE_CATEGORIES.flatMap((g) => g.items).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="live-diff-select"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Difficulty
              </label>
              <select
                id="live-diff-select"
                value={newDifficulty}
                onChange={(e) => setNewDifficulty(e.target.value)}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm text-[#FAFAFA]"
              >
                {DIFFICULTY_LEVELS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="live-qcount-input"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Question Quantity
              </label>
              <input
                id="live-qcount-input"
                type="number"
                min={3}
                max={25}
                value={newCount}
                onChange={(e) => setNewCount(Number(e.target.value))}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm font-mono text-[#FAFAFA]"
              />
            </div>

            <div>
              <label
                htmlFor="live-maxp-input"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Maximum Participants
              </label>
              <input
                id="live-maxp-input"
                type="number"
                min={10}
                max={2000}
                value={newMaxParticipants}
                onChange={(e) => setNewMaxParticipants(Number(e.target.value))}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm font-mono text-[#FAFAFA]"
              />
            </div>

            <div>
              <label
                htmlFor="live-basepts-input"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Base Points per Correct Answer
              </label>
              <input
                id="live-basepts-input"
                type="number"
                readOnly
                value={newBasePoints}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm font-mono text-[#A3A3A3]"
              />
            </div>

            <div>
              <label
                htmlFor="live-speed-input"
                className="block text-xs font-semibold text-[#D4D4D4] mb-1.5"
              >
                Max Speed Bonus Points
              </label>
              <input
                id="live-speed-input"
                type="number"
                readOnly
                value={newSpeedBonus}
                className="w-full rounded-xl border border-[#262626] bg-[#1C1C1C] px-3.5 py-2.5 text-sm font-mono text-[#A3A3A3]"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl border border-[#262626]">
            <label className="flex items-center gap-2.5 text-sm font-semibold text-[#FAFAFA] cursor-pointer">
              <input
                type="checkbox"
                checked={newTimerEnabled}
                onChange={(e) => setNewTimerEnabled(e.target.checked)}
                className="w-4 h-4 accent-[#525252] rounded"
              />
              <span>Enable Server Countdown Timer</span>
            </label>

            {newTimerEnabled && (
              <div className="flex items-center gap-2">
                <label
                  htmlFor="live-cd-input"
                  className="text-xs text-[#737373]"
                >
                  Seconds:
                </label>
                <input
                  id="live-cd-input"
                  type="number"
                  min={10}
                  max={90}
                  value={newCountdownDuration}
                  onChange={(e) =>
                    setNewCountdownDuration(Number(e.target.value))
                  }
                  className="w-20 rounded-lg border border-[#262626] bg-[#1C1C1C] px-2.5 py-1 text-sm font-mono text-[#FAFAFA]"
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isCreating}
            className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#EA580C] hover:brightness-110 text-white text-sm font-semibold transition-colors shadow-lg shadow-orange-950/30"
          >
            {isCreating
              ? 'Generating LIVE Room PIN & Validated Questions...'
              : 'Create LIVE Event & Open Host Dashboard'}
          </button>
        </form>
      )}
    </div>
  );
};
